import React, { useMemo, useState } from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import FantasyDriverDetailsDrawer from "./FantasyDriverDetailsDrawer";
import { lastNRaces, summarizeDriver } from "./fantasyInsights";
import { useDriverCarNumbers } from "../hooks/useDriverCarNumbers";
import { useDriverManufacturers } from "../hooks/useDriverManufacturers";
import { useFantasyDrawer } from "./hooks/useFantasyDrawer";
import { tableContainerSx } from "../styles/tableStyles";
import { stickyOnPhone, phoneNumberColumn, PHONE_NUMBER_COLUMN } from "../styles/responsive";
import ManufacturerLogo from "./ManufacturerLogo";

const BASES = [
  { key: "track", label: "At this track" },
  { key: "similar", label: "Similar tracks" },
  { key: "l5", label: "Last 5" },
  { key: "l10", label: "Last 10" },
];
const MEASURES = [
  { key: "fantasy_points", label: "Fantasy points" },
  { key: "race_pos", label: "Finish position" },
];

const headerSx = {
  fontWeight: "bold",
  textAlign: "center",
  bgcolor: (theme) => (theme.palette.mode === "dark" ? "#4F4F4F" : "grey.300"),
};

const percent = (rate) => `${Math.round(rate * 100)}%`;

// How reliable each driver is, not just how good on average: the best and worst result, the spread between
// races, and how often they finish in the top 10 or not at all
const FantasyConsistencyTable = ({ groupDrivers, trackRaces, similarTrackRaces, allRaces, currentSeasonRaces, pastSeasonRaces }) => {
  const [basis, setBasis] = useState("track");
  const [measure, setMeasure] = useState("fantasy_points");

  const { selectedDriver, handleDriverClick, closeDrawer } = useFantasyDrawer();
  const driverCarNumbers = useDriverCarNumbers(allRaces || currentSeasonRaces);
  const driverManufacturers = useDriverManufacturers(allRaces || currentSeasonRaces);

  const races = useMemo(() => {
    switch (basis) {
      case "similar": return similarTrackRaces;
      case "l5": return lastNRaces(currentSeasonRaces, 5);
      case "l10": return lastNRaces(currentSeasonRaces, 10);
      default: return trackRaces;
    }
  }, [basis, trackRaces, similarTrackRaces, currentSeasonRaces]);

  // Best average first; drivers without a race on this basis go last
  const rows = useMemo(() => {
    const summarized = groupDrivers.map((driver) => ({ driver, summary: summarizeDriver(races, driver, measure) }));
    return summarized.sort((a, b) => {
      if (!a.summary || !b.summary) return Boolean(b.summary) - Boolean(a.summary);
      return measure === "race_pos" ? a.summary.average - b.summary.average : b.summary.average - a.summary.average;
    });
  }, [groupDrivers, races, measure]);

  const floorHint = measure === "race_pos" ? "worst finish" : "fewest points";
  const ceilingHint = measure === "race_pos" ? "best finish" : "most points";

  return (
    <Paper sx={{ borderRadius: 3, p: 3, boxShadow: 3 }}>
      <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          {BASES.map(({ key, label }) => (
            <Chip key={key} label={label} color={basis === key ? "primary" : "default"} variant={basis === key ? "filled" : "outlined"} onClick={() => setBasis(key)} clickable />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          {MEASURES.map(({ key, label }) => (
            <Chip key={key} label={label} color={measure === key ? "primary" : "default"} variant={measure === key ? "filled" : "outlined"} onClick={() => setMeasure(key)} clickable />
          ))}
        </Stack>
      </Stack>

      <TableContainer sx={tableContainerSx}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.800" : "grey.200") }}>
              <TableCell sx={[{ fontWeight: "bold", px: 2 }, phoneNumberColumn, stickyOnPhone(0, { header: true })]}>#</TableCell>
              <TableCell sx={[{ fontWeight: "bold", px: 2 }, stickyOnPhone(PHONE_NUMBER_COLUMN, { header: true })]}>Driver</TableCell>
              <Tooltip title="Races on this basis. A handful of races is a thin sample.">
                <TableCell sx={headerSx}>Races</TableCell>
              </Tooltip>
              <Tooltip title="Average over those races">
                <TableCell sx={headerSx}>Avg</TableCell>
              </Tooltip>
              <Tooltip title={`Worst result (${floorHint})`}>
                <TableCell sx={headerSx}>Floor</TableCell>
              </Tooltip>
              <Tooltip title={`Best result (${ceilingHint})`}>
                <TableCell sx={headerSx}>Ceiling</TableCell>
              </Tooltip>
              <Tooltip title="Standard deviation: how far results swing from race to race. Lower is steadier.">
                <TableCell sx={headerSx}>Spread</TableCell>
              </Tooltip>
              <Tooltip title="Share of races finished in the top 10">
                <TableCell sx={headerSx}>Top 10</TableCell>
              </Tooltip>
              <Tooltip title="Share of races that ended in a crash or a failure">
                <TableCell sx={headerSx}>DNF</TableCell>
              </Tooltip>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map(({ driver, summary }, index) => (
              <TableRow
                key={driver}
                sx={{
                  bgcolor: index % 2 === 0 ? "background.default" : "action.hover",
                  "&:hover": { bgcolor: "action.selected", transition: "all 0.3s ease" },
                }}
              >
                <TableCell sx={[{ width: "40px", fontWeight: "bold", px: 2 }, phoneNumberColumn, stickyOnPhone(0, { odd: index % 2 === 1 })]}>{driverCarNumbers[driver] || "-"}</TableCell>
                <TableCell sx={[{ fontWeight: "bold", px: 2, cursor: "pointer" }, stickyOnPhone(PHONE_NUMBER_COLUMN, { odd: index % 2 === 1 })]} onClick={() => handleDriverClick(driver)}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ManufacturerLogo manufacturer={driverManufacturers[driver]} />
                    {driver}
                  </Box>
                </TableCell>
                {summary ? (
                  <>
                    <TableCell align="center">{summary.races}</TableCell>
                    <TableCell align="center">{summary.average.toFixed(1)}</TableCell>
                    <TableCell align="center">{summary.floor}</TableCell>
                    <TableCell align="center">{summary.ceiling}</TableCell>
                    <TableCell align="center">{summary.stdDev.toFixed(1)}</TableCell>
                    <TableCell align="center">{percent(summary.top10Rate)}</TableCell>
                    <TableCell align="center">{percent(summary.dnfRate)}</TableCell>
                  </>
                ) : (
                  <TableCell align="center" colSpan={7}>-</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Sorted by average, best first. Floor is the worst result and ceiling the best, so a high-ceiling driver with a low floor is a gamble.
      </Typography>

      {selectedDriver && (
        <FantasyDriverDetailsDrawer
          driver={selectedDriver}
          groupDrivers={groupDrivers}
          trackRaces={trackRaces}
          similarTrackRaces={similarTrackRaces}
          allRaces={allRaces}
          pastSeasonRaces={pastSeasonRaces}
          currentSeasonRaces={currentSeasonRaces}
          onClose={closeDrawer}
        />
      )}
    </Paper>
  );
};

export default FantasyConsistencyTable;
